import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NotificationsNone as EmptyIcon } from '../ui/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { AppNotification } from '../../types/notification';
import NotificationItem from './NotificationItem';

interface NotificationPanelProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ anchorEl, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);

  const panelStyle = useMemo<React.CSSProperties>(() => {
    if (!anchorEl) return {};
    const rect = anchorEl.getBoundingClientRect();
    const W = 380; // matches the panel width below
    const H = 480; // matches max-h
    const GAP = 8;
    const M = 8; // viewport margin
    // Horizontal: a left-side anchor (the desktop sidebar bell) opens to its
    // right; a right-side anchor (mobile top bar) right-aligns. Clamp on-screen.
    let left =
      rect.left < window.innerWidth / 2 ? rect.right + GAP : rect.right - W;
    left = Math.min(Math.max(M, left), window.innerWidth - W - M);
    // Vertical: open below if it fits, otherwise above the anchor — so a bell
    // pinned to the bottom of the sidebar no longer opens off-screen ("dead").
    if (rect.bottom + GAP + H > window.innerHeight - M) {
      return { bottom: window.innerHeight - rect.top + GAP, left, width: W };
    }
    return { top: rect.bottom + GAP, left, width: W };
  }, [anchorEl]);

  // Close on click-outside
  useEffect(() => {
    if (!anchorEl) return;
    const handler = (e: MouseEvent) => {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !anchorEl.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorEl, onClose]);

  const handleNotificationClick = async (notification: AppNotification) => {
    if (!notification.read) await markAsRead(notification.id);
    if (notification.type === 'company_review_request' && notification.data?.companyId) {
      navigate(`/companies/${notification.data.companyId}`);
    } else if (notification.data?.offerId) {
      navigate(
        notification.type === 'new_bid' || notification.type === 'offer_completed'
          ? '/freights'
          : '/exchange',
      );
    }
    onClose();
  };

  if (!anchorEl) return null;

  // Portal to <body>: the sidebar/navbar ancestors use backdrop-filter, which
  // makes them the containing block for position:fixed descendants — the panel
  // would be positioned relative to (and clipped by) the sidebar instead of the
  // viewport, appearing as a "dead" bell button.
  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[1400] w-[380px] max-h-[480px] rounded-2xl overflow-hidden border border-primary/20 shadow-pop bg-surface"
      style={panelStyle}
    >
      {/* Header */}
      <div className="px-4 py-3.5 flex justify-between items-center border-b border-line bg-primary/[0.03]">
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 rounded-full bg-primary" />
          <span className="text-sm font-black text-content">
            {t('notifications.title')}
          </span>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[0.7rem] font-extrabold text-primary bg-primary/10">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="text-xs font-bold text-primary hover:bg-primary/[0.08] px-2 py-1 rounded-lg transition-colors duration-150"
          >
            {t('notifications.mark_all_read')}
          </button>
        )}
      </div>

      {/* List / Empty state */}
      {notifications.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 opacity-50">
          <EmptyIcon size={40} className="text-primary opacity-40" />
          <p className="text-sm font-semibold text-muted">
            {t('notifications.no_notifications')}
          </p>
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[380px] divide-y divide-line">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onClick={handleNotificationClick}
            />
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
};

export default NotificationPanel;
